import { useCallback, useEffect, useMemo } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Loader,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  ArrowRightIcon,
  CheckIcon,
  LocationPinIcon,
  SuitcaseIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { viewJobConfirmation } from "@js/apps/jobs/actions";
import { useGetRelevantJobsQuery } from "@js/apps/jobs/api";
import { RouterLink } from "@js/components/link";
import { ModalInstance } from "@js/components/modal";
import { useAppDispatch, useNavigate } from "@js/hooks";
import type { SimilarJob } from "@js/types/jobs";
import {
  formatAbbreviatedBudget,
  getPaymentTypeSuffix,
} from "@js/utils/currency";

import { getLocationLabel } from "../../job-basic-details/utils";
import { BidCreateSuccessModalCloseButton } from "../bid-create-success-modal-close-button";

import styles from "./freelancer-screened-modal-content.module.scss";

type FreelancerScreenedModalContentProps = {
  closeModal?: () => void;
  roleId: number;
};

export const FreelancerScreenedModalContent = ({
  closeModal,
  roleId,
}: FreelancerScreenedModalContentProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const fetchRelevantJobsFilters = useMemo(
    () => ({ role: roleId, page_size: 3 }),
    [roleId],
  );

  const { data, isLoading } = useGetRelevantJobsQuery(fetchRelevantJobsFilters);
  const similarJobs = data?.results;

  const returnToJobsList = useCallback(() => {
    navigate("/jobs/");
    closeModal?.();
  }, [closeModal, navigate]);

  const viewApplications = () => {
    navigate("/talent/dashboard/my_jobs/my_proposals");
    closeModal?.();
  };

  useEffect(() => {
    dispatch(viewJobConfirmation());
  }, [dispatch]);

  if (isLoading) {
    return (
      <Stack className={styles.wrapper}>
        <Stack className={styles.topSection}>
          <Loader centered />
        </Stack>
      </Stack>
    );
  }

  const jobsCount = similarJobs?.length ?? 0;

  return (
    <Stack className={styles.wrapper}>
      <Stack className={styles.topSection}>
        <BidCreateSuccessModalCloseButton
          variant="gray-yellow"
          onClick={returnToJobsList}
          sx={{
            position: "absolute",
            top: "28px",
            right: "32px",
          }}
        />
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            width: "64px",
            height: "64px",
            borderRadius: "18px",
            boxShadow: "0px 4px 12px 0px #14141714",
            backgroundColor: "var(--white)",
            mb: "20px",
          }}
        >
          <CheckIcon sx={{ color: "var(--dark-yellow)", fontSize: "28px" }} />
        </Box>
        <Chip
          color="yellow"
          sx={{
            padding: "5px 12px !important",
            mb: 1,
            border: "1px solid #AA69082E",
          }}
          label={
            <Typography
              component="p"
              variant="paragraph"
              size="medium"
              fontWeight={500}
              sx={{
                color: "var(--dark-yellow) !important",
                fontSize: "11px !important",
                "--dot-size": "5px",
              }}
              className="dot-prefix uppercase--wide"
            >
              Application received
            </Typography>
          }
        />

        <Typography
          component="p"
          variant="title"
          size="medium"
          sx={{
            fontSize: { xs: "24px !important", md: "32px !important" },
            mb: { xs: 2, sm: 3 },
          }}
        >
          Thanks for applying!
        </Typography>
        <Typography
          component="p"
          size="medium"
          sx={{ fontSize: { xs: "13px !important", sm: "15px !important" } }}
        >
          Here are some other jobs that may be a good match.
          <br />
          Apply now.
        </Typography>
      </Stack>

      <Box className={styles.bottomSection}>
        <Stack
          direction="row"
          sx={{ alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2 }}
        >
          <Typography
            component="p"
            variant="label"
            fontWeight={500}
            sx={{ fontSize: "14px !important" }}
          >
            Other jobs you might like
          </Typography>
          <Chip
            size="x-small"
            color="violet"
            LabelProps={{ sx: { lineHeight: "1 !important" } }}
            label={
              <Typography
                component="label"
                sx={{ fontSize: "11px !important" }}
                fontWeight={500}
              >
                {jobsCount} {jobsCount === 1 ? "match" : "matches"}
              </Typography>
            }
          />
          <Button
            size="x-small"
            onClick={() => returnToJobsList()}
            variant="transparent-dark-violet"
            endIcon={
              <ArrowRightIcon sx={{ fontSize: "11px", color: "inherit" }} />
            }
            sx={{
              ml: "auto",
              mr: -1,
              textDecoration: "underline !important",
              fontWeight: 500,
            }}
          >
            View all jobs
          </Button>
        </Stack>

        {jobsCount ? (
          <Stack
            sx={{
              border: `1px solid var(--medium-violet)`,
              borderRadius: 4,
              mb: 3,
            }}
          >
            {similarJobs?.map((job, index) => {
              const isLast = index === jobsCount - 1;
              return <JobItem key={job.id} job={job} isLast={isLast} />;
            })}
          </Stack>
        ) : (
          <Typography
            component="p"
            color="grey-2"
            sx={{ fontStyle: "italic", mb: 3 }}
          >
            No matching jobs right now — check back soon, new roles are added
            all the time.
          </Typography>
        )}

        <div className={styles.ctaButtons}>
          <Button
            variant="dark-violet"
            onClick={returnToJobsList}
            shape="squared"
            endIcon={
              <ArrowRightIcon sx={{ fontSize: "14px", color: "inherit" }} />
            }
          >
            Browse more jobs
          </Button>
          <Button
            variant="white-violet"
            onClick={viewApplications}
            shape="squared"
          >
            View application
          </Button>
        </div>
      </Box>
      <Alert withIcon alignIcon={"center"}>
        <Typography component="p" sx={{ fontSize: "13px !important" }}>
          <Typography
            component="span"
            sx={{ fontSize: "inherit !important" }}
            fontWeight={500}
          >
            Only accept job offers on Braintrust.
          </Typography>{" "}
          For more info on staying safe, visit our{" "}
          <Typography
            component="link"
            href={SETTINGS.SUPPORT_URLS.REPORT_SCAMS_AND_FRAUD_ARTICLE_URL}
            target="_blank"
            RouterLink={RouterLink}
            fontWeight={500}
            sx={{ fontSize: "inherit!important", color: "var(--dark-yellow)" }}
          >
            help center
          </Typography>
          .
        </Typography>
      </Alert>
    </Stack>
  );
};

const DEFAULT_AVATAR_BG = "--soft-violet";
const DEFAULT_AVATAR_COLOR = "--darm-violet";

const JobItem = ({ job, isLast }: { job: SimilarJob; isLast: boolean }) => {
  const { logo_background_color, name, name_color } = job.employer;

  const locationLabel = useMemo(
    () => getLocationLabel(job.locations),
    [job.locations],
  );

  const budget = useMemo(
    () =>
      formatAbbreviatedBudget(job.budget_minimum_usd, job.budget_maximum_usd),
    [job],
  );
  const budgetSuffix = getPaymentTypeSuffix(job.payment_type);

  return (
    <Stack
      direction="row"
      sx={{
        gap: "14px",
        alignItems: "center",
        borderBottom: !isLast ? "1px solid var(--medium-violet)" : undefined,
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "44px",
          height: "44px",
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          background: `var(${logo_background_color || DEFAULT_AVATAR_BG})`,
        }}
      >
        <Typography
          component="span"
          size="large"
          fontWeight={500}
          sx={{
            textTransform: "uppercase",
            color: `var(${name_color || DEFAULT_AVATAR_COLOR})`,
          }}
        >
          {name.charAt(0)}
        </Typography>
      </Box>
      <Stack sx={{ mr: "auto" }}>
        <Typography
          variant="link"
          RouterLink={RouterLink}
          to={`/employers/${job.employer.id}/?job=${job.id}`}
          target="_blank"
          component="span"
          sx={{
            fontSize: "11px !important",
            color: "var(--grey-2)",
            textTransform: "uppercase",
            letterSpacing: "0.6px !important",
            lineHeight: "14px !important",
            mb: "3px",
            "&:not(:hover)": {
              textDecoration: "none",
            },
          }}
        >
          {job.employer.name}
        </Typography>
        <Typography
          component="p"
          sx={{
            fontSize: "15px !important",
            lineHeight: "18px !important",
            mb: 0.25,
          }}
          fontWeight={500}
        >
          {job.title}
        </Typography>
        <Stack
          direction="row"
          sx={{ gap: 1, alignItems: "center", flexWrap: "wrap", rowGap: 0.25 }}
        >
          <Typography
            component="span"
            color="grey-2"
            sx={{ fontSize: "12px !important", whiteSpace: "nowrap" }}
          >
            <LocationPinIcon
              style={{ fontSize: "11px", verticalAlign: "text-top" }}
            />{" "}
            {locationLabel}
          </Typography>
          <Typography
            component="span"
            color="grey-2"
            sx={{ fontSize: "12px !important", whiteSpace: "nowrap" }}
          >
            <SuitcaseIcon
              style={{ fontSize: "11px", verticalAlign: "text-top" }}
            />{" "}
            {ENUMS.JobTypeLabels[job.job_type]}
          </Typography>
        </Stack>
      </Stack>
      <Typography
        component="p"
        fontWeight={500}
        sx={{
          fontSize: "15px !important",
          display: { xs: "none", sm: "block" },
        }}
      >
        {budget}
        <Typography
          component="span"
          color="grey-2"
          sx={{ fontSize: "12px !important" }}
          fontWeight={400}
        >
          {budgetSuffix}
        </Typography>
      </Typography>
      <IconButton
        sx={{ p: "9px !important" }}
        shape="squared"
        variant="violet"
        aria-label="Go to Job"
        RouterLink={RouterLink}
        to={`/jobs/${job.id}`}
      >
        <ArrowRightIcon sx={{ fontSize: "13px" }} />
      </IconButton>
    </Stack>
  );
};

export const openFreelancerScreenedModal = ({ roleId }: { roleId: number }) => {
  ModalInstance.open({
    children: (
      <FreelancerScreenedModalContent
        roleId={roleId}
        closeModal={() => ModalInstance.close()}
      />
    ),
    padding: false,
    className: styles.modal,
    closeButton: false,
  });
};
